import React, { useEffect, useState, useCallback, forwardRef, useImperativeHandle } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  CardMedia,
  LinearProgress,
  IconButton,
  Modal,
  Backdrop,
  Fade,
  Paper,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import OpenInFullIcon from "@mui/icons-material/OpenInFull"; // Changed from ZoomInIcon
import CloseIcon from "@mui/icons-material/Close";
import SpeedIcon from "@mui/icons-material/Speed";
import MarkdownRenderer from "./MarkdownRenderer";
import AudioComponent from "./AudioComponent";
import api from "../../api";
import decodeNewlines from "../../utils/stringUtils";
import '../../assets/concept-tags.css'

import SpeechSynthesisAudioComponent from './SpeechSynthesisComponent';

// Step transition duration in ms
const STEP_TRANSITION_DURATION = 150;

const MultiStepCardViewer = forwardRef(({ 
  cardData: propCardData, 
  language = 'en', 
  onNextCard = null, 
  onPrevCard = null 
}, ref) => {
  // console.log('MultiStepCardViewer props: ', propCardData, ' language: ', language)
  const { cardId } = useParams();

  const [cardData, setCardData] = useState(propCardData || null);
  const [loading, setLoading] = useState(propCardData ? false : true);
  const [stepIndex, setStepIndex] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [audioData, setAudioData] = useState(propCardData?.audioData || null);
  const [audioCdnUrl, setAudioCdnUrl] = useState(propCardData?.audioCdnUrl || null);
  const [contentHeight, setContentHeight] = useState(0); // State to track content height
  const [stepFadeIn, setStepFadeIn] = useState(true); // For step transition
  const [isStepTransitioning, setIsStepTransitioning] = useState(false); // Track step transitions

  // Ref for content card to measure its height
  const contentRef = React.useRef(null);

  // We need to customize the AudioComponent to support Web Speech API
  const [speechSynth, setSpeechSynth] = useState(null);

  // Fetch card data only if not provided by parent
  useEffect(() => {
    if (propCardData) {
      let test = decodeNewlines(propCardData);
      console.log('Language ', language)
      setCardData(test);
      
      // Check for audio sources - prioritize CDN URL
      if (propCardData.audioCdnUrl) {
        setAudioCdnUrl(propCardData.audioCdnUrl);
        setAudioData(null); // Clear audioData if CDN URL is available
      } else if (propCardData.audioData && Object.keys(propCardData.audioData).length) {
        setAudioData(propCardData.audioData);
      } else {
        // console.log('null audio')
        setAudioData(null);
        setAudioCdnUrl(propCardData.audio.path);
        try {
          // Check if speech synthesis is available
          if ('speechSynthesis' in window) {
            // Create custom speech synthesis handler
            const synth = window.speechSynthesis;
            console.log('Attempting speech synthesis for ', cardData.title , ' language ', language);
            const utterance = new SpeechSynthesisUtterance(cardData.title || "");
            utterance.lang = language;
            
            console.log('Synth object ', synth)
            setSpeechSynth({
              utterance,
              synth,
              play: () => {
                console.log('Invoking play function with');
                synth.cancel(); // Cancel any ongoing speech
                synth.speak(utterance);
              },
              stop: () => {
                console.log('Invoking cancel function');
                synth.cancel();
              }
            });
          } else {
            console.warn("Speech synthesis not supported in this browser");
            alert('Speech Synthesis not supported in this browser')
          }
        } catch (error) {
          console.error("Error setting up speech synthesis:", error);
          alert("Error setting up speech synthesis:", error)
        }
      }
      setLoading(false);
    } else {
      const fetchCard = async () => {
        try {
          const response = await api.get(`/multi-step-card/${cardId}`);
          if (response.data && response.data.card) {
            setCardData(response.data.card);
            
            // Check for audio sources - prioritize CDN URL
            if (response.data.card.audioCdnUrl) {
              setAudioCdnUrl(response.data.card.audioCdnUrl);
            } else {
              setAudioData(response.data.card.audioData || null);
            }
          }
        } catch (error) {
          console.error("Error fetching card:", error);
        } finally {
          setLoading(false);
        }
      };
      fetchCard();
    }
  }, [cardId, propCardData]);

  // When a new card is loaded, ensure fadeIn is true
  useEffect(() => {
    setStepFadeIn(true);
  }, [propCardData]);

  // Handle step transition with fade effect
  const changeStep = useCallback((newIndex) => {
    if (isStepTransitioning) return;
    
    setIsStepTransitioning(true);
    setStepFadeIn(false);
    
    setTimeout(() => {
      setStepIndex(newIndex);
      setStepFadeIn(true);
      
      setTimeout(() => {
        setIsStepTransitioning(false);
      }, STEP_TRANSITION_DURATION);
    }, STEP_TRANSITION_DURATION);
  }, [isStepTransitioning]);

  // Handle step navigation via keyboard
  const handleKeyDown = useCallback(
    (event) => {
      if (!cardData || isStepTransitioning) return;
      const steps = cardData.steps.length;
      
      if (event.key === "ArrowLeft") {
        if ((steps === 1 || stepIndex === 0) && onPrevCard && !event.shiftKey) {
          // If there's only one step or at first step, go to previous card
          onPrevCard();
        } else if (stepIndex > 0) {
          changeStep(stepIndex - 1);
        }
      } else if (event.key === "ArrowRight") {
        if ((steps === 1 || stepIndex === steps - 1) && onNextCard && !event.shiftKey) {
          // If there's only one step or at last step, go to next card
          onNextCard();
        } else if (stepIndex < steps - 1) {
          changeStep(stepIndex + 1);
        }
      } else if (event.key === "Escape" && modalOpen) {
        setModalOpen(false);
      }
    },
    [cardData, modalOpen, stepIndex, onPrevCard, onNextCard, isStepTransitioning, changeStep]
  );

  // Update content height when step changes or window resizes
  useEffect(() => {
    const updateContentHeight = () => {
      if (contentRef.current) {
        setContentHeight(contentRef.current.clientHeight);
      }
    };

    // Initial calculation
    updateContentHeight();
    
    // Add resize listener
    window.addEventListener('resize', updateContentHeight);
    
    // Update when content changes (step changes)
    const observer = new MutationObserver(updateContentHeight);
    if (contentRef.current) {
      observer.observe(contentRef.current, { childList: true, subtree: true });
    }

    return () => {
      window.removeEventListener('resize', updateContentHeight);
      observer.disconnect();
    };
  }, [stepIndex, cardData]);

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  // Expose the current step status and a setter via ref
  useImperativeHandle(ref, () => ({
    getStatus: () => {
      if (!cardData) return { currentStep: 0, totalSteps: 0 };
      return {
        currentStep: stepIndex,
        totalSteps: cardData.steps.length
      };
    },
    setStep: (newStep) => {
      if (newStep !== stepIndex) {
        changeStep(newStep);
      }
    }
  }));

  // Handle opening the image in a modal
  const handleOpenModal = (image) => {
    setSelectedImage(image);
    setModalOpen(true);
  };

  // Handle closing the modal
  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedImage(null);
  };

  const getTypographyVariant = (title) => {
    if (!title) return "p";
    const length = title.length;
    if (length <= 10) return "h4"; // Reduced from h1
    if (length <= 20) return "h5"; // Reduced from h2
    if (length <= 40) return "h6"; // Reduced from h3
    return "subtitle1";  // Reduced from h4
  };

  if (loading) {
    return (
      <Box sx={{ width: "100%", mt: 2 }}>
        <LinearProgress />
        <Typography sx={{ m: 1, textAlign: "center" }}>Loading...</Typography>
      </Box>
    );
  }

  if (!cardData) {
    return (
      <Box sx={{ 
        m: 0,
        textAlign: "center"
      }}>
        <Typography variant="h6">Card not found</Typography>
        <Typography color="text.secondary">
          The requested card could not be loaded
        </Typography>
      </Box>
    );
  }

  const totalSteps = cardData.steps.length;
  const stepContent = cardData.steps[stepIndex].content;
  const stepImages = cardData.steps[stepIndex].images || [];

  const handleNext = () => {
    const steps = cardData.steps.length;
    if (steps === 1 && onNextCard) {
      // If there's only one step, go to the next card
      onNextCard();
    } else if (stepIndex === steps - 1 && onNextCard) {
      // If at the last step, go to the next card
      onNextCard();
    } else if (stepIndex < steps - 1 && !isStepTransitioning) {
      // Otherwise, go to the next step in the current card
      changeStep(stepIndex + 1);
    }
  };
  
  const handlePrev = () => {
    const steps = cardData.steps.length;
    if (steps === 1 && onPrevCard) {
      // If there's only one step, go to the previous card
      onPrevCard();
    } else if (stepIndex === 0 && onPrevCard) {
      // If at the first step, go to the previous card
      onPrevCard();
    } else if (stepIndex > 0 && !isStepTransitioning) {
      // Otherwise, go to the previous step in the current card
      changeStep(stepIndex - 1);
    }
  };

  const progress = ((stepIndex + 1) / totalSteps) * 100;

  return (
    <Box sx={{
      background: 'rgb(216, 241, 247)',
      minHeight: '80vh', // Reduced from 100vh
      py: 2, // Reduced from 4
      overflowX: 'hidden', // Added to disable horizontal scroll
      color: 'black !important'
    }}>
      
      <Container maxWidth="md" sx={{ overflowX: 'hidden' }}> {/* Added overflow hidden */}
        <Paper
          elevation={3} // Reduced from 6
          sx={{
            borderRadius: 2, // Reduced from 4
            overflow: 'hidden',
            background: 'white',
            boxShadow: '0 5px 15px rgba(0,0,0,0.05)' // Reduced shadow
          }}
        >
          {/* Hero area with reduced padding */}
          <Box
            sx={{
              background: 'rgb(45, 27, 148)',
              color: '#fff',
              py: 1.5, // Reduced from 3
              position: "relative",
              boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.08)" // Reduced shadow
            }}
          >
            <Box sx={{ display: 'flex', position: 'relative', padding: "1.5%" }}> {/* Reduced padding */}
              {/* Updated Audio Component - now passes both audio sources */}
              {audioCdnUrl || audioData ? (
                <AudioComponent audioCdnUrl={audioCdnUrl} audioData={audioData} />
              ) : speechSynth ? (
                <SpeechSynthesisAudioComponent speechSynthData={speechSynth} />
              ) : null}

              <Typography
                variant={getTypographyVariant(cardData.title)}
                sx={{
                  fontWeight: 600,
                  // letterSpacing: '-0.5px',
                  textShadow: '1px 1px 1px rgba(0,0,0,0.1)', // Reduced shadow
                  fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
                  ml: 12, // Reduced from 14
                  p: 2
                }}
              >
                {cardData.title}
              </Typography>
            </Box>

            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                height: 3, // Reduced from 4
                backgroundColor: 'rgba(255,255,255,0.2)',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: '#fff'
                }
              }}
            />
          </Box>

          {/* Main content with reduced padding */}
          <Box sx={{ p: { xs: 1.5, md: 2.5 }, overflowX: 'hidden' }}> {/* Added overflowX: hidden */}
            {/* Navigation buttons - REMOVED disabled prop for parity with keyboard behavior */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 2, // Reduced from 3
                gap: 1 // Reduced from 2
              }}
            >
              <IconButton
                onClick={handlePrev}
                disabled={isStepTransitioning}
                size="small" // Added small size
                sx={{
                  background: 'rgba(25, 118, 210, 0.1)',
                  borderRadius: 1.5, // Reduced from 2
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    background: 'rgba(25, 118, 210, 0.2)'
                  }
                }}
              >
                <KeyboardArrowLeftIcon />
              </IconButton>

              <IconButton
                onClick={handleNext}
                disabled={isStepTransitioning}
                size="small" // Added small size
                sx={{
                  background: 'rgba(25, 118, 210, 0.1)',
                  borderRadius: 1.5, // Reduced from 2
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    background: 'rgba(25, 118, 210, 0.2)'
                  }
                }}
              >
                <KeyboardArrowRightIcon />
              </IconButton>
            </Box>

            {/* Content and Images with reduced gap */}
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                gap: 2, // Reduced from 4
                position: "relative",
                overflowX: 'hidden' // Added to disable horizontal scroll
              }}
            >
              <Fade in={stepFadeIn} timeout={STEP_TRANSITION_DURATION}>
                <Card
                  ref={contentRef}
                  sx={{
                    flex: 1,
                    boxShadow: "0 3px 10px rgba(0,0,0,0.06)", // Reduced shadow
                    borderRadius: 2, // Reduced from 3
                    backgroundColor: "#f9fafb",
                    minHeight: 200, // Reduced from 300
                    overflowX: 'hidden' // Added to disable horizontal scroll
                  }}
                >
                  <CardContent
                    sx={{
                      p: 2, // Reduced from 3
                      '& h4': {
                        fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
                        color: '#2c3e50',
                        paddingBottom: 0.5, // Reduced from 1
                        marginBottom: 1, // Reduced from 2
                        width: 'fit-content'
                      },
                      lineHeight: '1.4 !important', // Reduced from 1.5
                      fontFamily: 'Helvetica',
                      fontSize: '0.9rem', // Added smaller font size
                      overflowX: 'hidden' // Added to disable horizontal scroll
                    }}
                  >
                    <MarkdownRenderer content={stepContent} />
                  </CardContent>
                </Card>
              </Fade>

              {stepImages.length > 0 && (
                <Fade in={stepFadeIn} timeout={STEP_TRANSITION_DURATION}>
                  <Box
                    sx={{
                      width: { xs: "100%", md: 220 }, // Reduced from 300
                      display: "flex",
                      flexDirection: "column",
                      height: { xs: "auto", md: contentHeight > 0 ? contentHeight : "auto" },
                      overflowY: { xs: "visible", md: "auto" },
                      overflowX: "hidden", // Already had overflowX: hidden
                      transition: "all 0.3s ease"
                    }}
                  >
                    {stepImages.map((img, idx) => (
                      <Card
                        key={idx}
                        sx={{
                          mb: 1.5, // Reduced from 2
                          boxShadow: "0 2px 4px rgba(0,0,0,0.08)", // Reduced shadow
                          transition: "all 0.2s ease",
                          "&:hover": {
                            transform: "translateY(-2px)", // Reduced from -4px
                            boxShadow: "0 3px 6px rgba(0,0,0,0.12)" // Enhanced shadow on hover
                          },
                          position: "relative",
                          flex: stepImages.length === 1 ? "1" : "none", // If only one image, it can stretch
                          display: "flex",
                          flexDirection: "column"
                        }}
                        onClick={() => handleOpenModal(img)} // Added click handler to entire card for better UX
                      >
                        <Box 
                          sx={{ 
                            position: "relative",
                            flex: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            overflow: "hidden", // Prevent image overflow
                            backgroundColor: "#f5f5f5",
                            borderTopLeftRadius: 8,
                            borderTopRightRadius: 8
                          }}
                          onDoubleClick={() => handleOpenModal(img)} // Added double-click handler
                        >
                          <CardMedia
                            component="img"
                            // Prioritize CDN URL over base64 data
                            image={img.cdnUrl || img.dataUrl || img.path}
                            alt={img.originalName}
                            sx={{
                              objectFit: "contain",
                              maxHeight: 180, // Reduced from 250
                              width: "100%",
                              transition: "transform 0.3s ease",
                              "&:hover": {
                                transform: "scale(1.02)" // Subtle zoom effect on hover
                              }
                            }}
                          />
                          <IconButton
                            sx={{
                              position: "absolute",
                              top: 4, // Reduced from 8
                              right: 4, // Reduced from 8
                              backgroundColor: "rgba(255, 255, 255, 0.8)",
                              "&:hover": {
                                backgroundColor: "rgba(255, 255, 255, 0.9)"
                              },
                              width: 28, // Reduced from 36
                              height: 28, // Reduced from 36
                              padding: 0.5, // Added smaller padding
                              boxShadow: "0 1px 3px rgba(0,0,0,0.1)" // Added subtle shadow
                            }}
                            onClick={(e) => {
                              e.stopPropagation(); // Prevent card click from triggering
                              handleOpenModal(img);
                            }}
                            size="small" // Added small size
                          >
                            <OpenInFullIcon fontSize="small" /> {/* Changed to expand icon */}
                          </IconButton>
                        </Box>
                        <CardContent 
                          sx={{ 
                            py: 1, 
                            px: 1.5, 
                            backgroundColor: "#fff",
                            borderBottomLeftRadius: 8,
                            borderBottomRightRadius: 8,
                            borderTop: "1px solid rgba(0,0,0,0.03)" // Subtle separator
                          }}
                        >
                          <Typography
                            variant="body2" // Changed from caption
                            sx={{
                              fontWeight: 500,
                              color: "#555",
                              textAlign: "center",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              fontSize: '0.8rem', // Better font size
                              fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
                              // Removed italic style
                            }}
                          >
                            {img.originalName}
                          </Typography>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                </Fade>
              )}
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mt: 2.5, // Reduced from 4
                alignItems: "center",
                flexDirection: "column",
                gap: 1 // Reduced from 2
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  gap: 0.5, // Reduced from 1
                  alignItems: "center"
                }}
              >
                {Array.from({ length: totalSteps }).map((_, idx) => (
                  <Box
                    key={idx}
                    onClick={() => {
                      if (!isStepTransitioning && idx !== stepIndex) {
                        changeStep(idx);
                      }
                    }}
                    sx={{
                      width: idx === stepIndex ? 10 : 6, // Reduced from 12/8
                      height: idx === stepIndex ? 10 : 6, // Reduced from 12/8
                      borderRadius: "50%",
                      backgroundColor: idx === stepIndex ? "#1976d2" : "#c5c5c5", // Removed conditional styling for answer step
                      cursor: isStepTransitioning ? "default" : "pointer",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        backgroundColor: isStepTransitioning ? (idx === stepIndex ? "#1976d2" : "#c5c5c5") : (idx === stepIndex ? "#1976d2" : "#999") // Removed conditional styling for answer step
                      },
                      opacity: isStepTransitioning ? 0.7 : 1
                    }}
                  />
                ))}
              </Box>

              {/* Only show step counter if there's more than one step */}
              {totalSteps > 1 && (
                <Typography
                  variant="caption" // Changed from body2
                  sx={{
                    color: "text.secondary",
                    fontStyle: "italic",
                    fontSize: '0.7rem' // Added smaller font
                  }}
                >
                  {`Step ${stepIndex + 1} of ${totalSteps}`}
                </Typography>
              )}

              <Box
                sx={{
                  display: "flex",
                  gap: 1.5, // Reduced from 2
                  mt: 0.5 // Reduced from 1
                }}
              >
                <Button
                  variant="outlined"
                  onClick={handlePrev}
                  disabled={isStepTransitioning}
                  startIcon={<ArrowBackIosNewIcon fontSize="small" />} // Added small icon
                  size="small" // Added small size
                  sx={{
                    borderRadius: 12, // Reduced from 20
                    px: 2, // Reduced from 3
                    fontSize: '0.75rem' // Added smaller font
                  }}
                >
                  Previous
                </Button>

                <Button
                  variant="contained"
                  onClick={handleNext}
                  disabled={isStepTransitioning}
                  endIcon={<ArrowForwardIosIcon fontSize="small" />} // Added small icon
                  size="small" // Added small size
                  sx={{
                    borderRadius: 12, // Reduced from 20
                    px: 2, // Reduced from 3
                    boxShadow: "0 1px 3px rgba(0,0,0,0.15)", // Reduced shadow
                    fontSize: '0.75rem' // Added smaller font
                  }}
                >
                  Next
                </Button>
              </Box>
            </Box>
          </Box>
        </Paper>
      </Container>

      {/* Image Modal - Enhanced for better viewing experience */}
      <Modal
        open={modalOpen}
        onClose={handleCloseModal}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 3 // Reduced from 4
        }}
      >
        <Fade in={modalOpen}>
          <Box
            sx={{
              position: "relative",
              maxWidth: "90vw",
              maxHeight: "90vh",
              backgroundColor: "white",
              borderRadius: 1.5, // Reduced from 2
              boxShadow: "0 10px 25px rgba(0,0,0,0.2)", // Enhanced modal shadow
              p: 1.5, // Reduced from 2
              outline: "none",
              overflow: "hidden" // Prevent content overflow
            }}
          >
            <IconButton
              onClick={handleCloseModal}
              size="small" // Added small size
              sx={{
                position: "absolute",
                right: 6, // Reduced from 8
                top: 6, // Reduced from 8
                color: "#333",
                zIndex: 10,
                backgroundColor: "rgba(255,255,255,0.9)",
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                "&:hover": {
                  backgroundColor: "white",
                  color: "#000"
                }
              }}
            >
              <CloseIcon fontSize="small" /> {/* Added small icon */}
            </IconButton>
            {selectedImage && (
              <>
                <Box
                  component="img"
                  // Prioritize cdnUrl over dataUrl in modal view too
                  src={selectedImage.cdnUrl || selectedImage.dataUrl}
                  alt={selectedImage.originalName}
                  sx={{
                    width: "100%",
                    height: "auto",
                    maxHeight: "calc(90vh - 80px)", // Reduced from -100px
                    objectFit: "contain",
                    display: "block",
                    m: "0 auto",
                    borderRadius: 1 // Add subtle rounded corners to image
                  }}
                />
                <Box
                  sx={{
                    mt: 1.5, // Reduced from 2
                    textAlign: "center",
                    backgroundColor: "rgba(0,0,0,0.02)",
                    borderRadius: 1,
                    p: 1,
                    maxWidth: "80%",
                    mx: "auto"
                  }}
                >
                  <Typography
                    variant="body2" // Changed from subtitle1
                    sx={{
                      fontWeight: 500,
                      color: "#333",
                      fontSize: '0.85rem', // Better font size
                      fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                    }}
                  >
                    {selectedImage.originalName}
                  </Typography>
                </Box>
              </>
            )}
          </Box>
        </Fade>
      </Modal>
    </Box>
  );
});

export default MultiStepCardViewer;